import { useState } from "react";
import { toast } from "sonner";
import { get, put, errMsg, label } from "@/lib/api";
import { PageHeader, Card, Btn, Loading, useFetch } from "@/components/kit";

function JsonSetting({ k, value, onSaved }) {
  const [txt, setTxt] = useState(JSON.stringify(value, null, 2));
  const save = async () => {
    let v;
    try { v = JSON.parse(txt); } catch (e) { return toast.error("Invalid JSON"); }
    try { await put(`/settings/${k}`, { value: v }); toast.success(`${label(k)} saved`); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card title={label(k)} testid={`setting-${k}`} action={<Btn className="h-7 px-3 text-xs" data-testid={`save-setting-${k}`} onClick={save}>Save</Btn>}>
      <textarea className="fo-input font-mono text-xs" rows={8} value={txt} onChange={(e) => setTxt(e.target.value)} />
    </Card>
  );
}

export function SettingsPage() {
  const { data, reload } = useFetch(() => get("/settings"), []);
  const tpl = useFetch(() => get("/notification-templates"), []);
  const logs = useFetch(() => get("/notification-logs"), []);
  if (!data) return <Loading />;
  const flags = data.feature_flags || {};
  const toggle = async (f) => { try { await put("/settings/feature_flags", { value: { ...flags, [f]: !flags[f] } }); reload(); toast.success(`${f} ${!flags[f] ? "enabled" : "disabled"}`); } catch (e) { toast.error(errMsg(e)); } };
  const saveTpl = async (t, patchBody) => { await put(`/notification-templates/${t.event}`, patchBody); tpl.reload(); };
  return (
    <div className="space-y-6">
      <PageHeader title="Settings & configuration" subtitle="Configure without developer changes. Every change is audited." />
      <Card title="Feature flags" testid="feature-flags">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Object.keys(flags).map((f) => (
            <label key={f} className="flex items-center justify-between rounded-lg border p-3 text-sm"><span className="font-mono text-xs">{f}</span>
              <input type="checkbox" data-testid={`flag-${f}`} checked={!!flags[f]} onChange={() => toggle(f)} /></label>
          ))}
        </div>
      </Card>
      <div className="grid gap-6 lg:grid-cols-2">
        {["sla_rules", "assignment", "lead_scoring", "duplicate_rules", "company", "policies", "consent_texts", "retention", "dropoff_reminders", "white_label"].map((k) => <JsonSetting key={k} k={k} value={data[k]} onSaved={reload} />)}
      </div>
      <Card title="Notification templates">
        {(tpl.data || []).map((t) => (
          <div key={t.event} className="mb-3 grid items-center gap-2 border-b pb-3 md:grid-cols-6">
            <span className="font-mono text-xs">{t.event}</span>
            <input className="fo-input md:col-span-3" defaultValue={t.body} onBlur={(e) => e.target.value !== t.body && saveTpl(t, { body: e.target.value })} />
            <div className="flex gap-2 text-xs md:col-span-2">{["sms", "email", "whatsapp", "push"].map((c) => <label key={c} className="flex items-center gap-1"><input type="checkbox" checked={(t.channels || []).includes(c)} onChange={() => saveTpl(t, { channels: (t.channels || []).includes(c) ? t.channels.filter((x) => x !== c) : [...(t.channels || []), c] })} />{c}</label>)}</div>
          </div>
        ))}
      </Card>
      <Card title="Notification log (MOCK providers)"><div className="max-h-64 overflow-auto text-xs">{(logs.data || []).map((l) => <p key={l.id} className="border-b py-1"><b>{l.channel}</b> → {l.to || "—"} · {l.event} · {l.subject} · {l.status}</p>)}</div></Card>
    </div>
  );
}

export function Roles() {
  const { data, reload } = useFetch(() => get("/roles"), []);
  const [busy, setBusy] = useState(null);
  if (!data) return <Loading />;
  const toggle = async (r, p) => {
    if (r.code === "super_admin") return;
    const perms = r.permissions.includes(p) ? r.permissions.filter((x) => x !== p) : [...r.permissions, p];
    setBusy(r.code + p);
    try { await put(`/roles/${r.code}`, { permissions: perms }); reload(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(null);
  };
  return (
    <div className="space-y-4">
      <PageHeader title="Roles & permissions" subtitle="Granular permission matrix. Super Admin has all permissions." />
      <div className="overflow-auto rounded-xl border bg-white" data-testid="permission-matrix">
        <table className="text-xs">
          <thead className="sticky top-0 bg-slate-50"><tr><th className="px-3 py-2 text-left">Permission</th>{data.roles.map((r) => <th key={r.code} className="px-2 py-2">{r.label}</th>)}</tr></thead>
          <tbody>{data.permissions.map((p) => (
            <tr key={p} className="border-t"><td className="px-3 py-1.5 font-mono">{p}</td>
              {data.roles.map((r) => <td key={r.code} className="px-2 text-center"><input type="checkbox" data-testid={`perm-${r.code}-${p}`} disabled={r.code === "super_admin" || busy === r.code + p} checked={r.permissions.includes("*") || r.permissions.includes(p)} onChange={() => toggle(r, p)} /></td>)}</tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}
